import React, { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { CheckCircle2, Loader2, TriangleAlert, XCircle } from "lucide-react";
import { ApiError } from "@/services/BaseService";
import { formatDateTimeCompact } from "@/utils/dateUtils";
import {
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useCancelDeletionJob, useDeletionJob } from "@/hooks/useDataDeletion";
import {
  DeletionJob,
  DeletionJobStatus,
  DeletionProgressStep,
} from "@/types/data-deletion";
import { DeletionStatusBadge } from "./DeletionStatusBadge";

interface DeletionJobStatusViewProps {
  jobId: string;
  /** Fires once, when the job reaches COMPLETED — used to invalidate lists. */
  onCompleted?: (job: DeletionJob) => void;
  onClose: () => void;
  /**
   * Whether to offer the Cancel action for a PENDING job. Defaults to true; pass false
   * for callers that cannot cancel (e.g. a HOTEL_ADMIN, whose role can't hit the
   * SUPER_ADMIN-only cancel endpoint).
   */
  canCancel?: boolean;
}

const ProgressRow: React.FC<{ step: DeletionProgressStep }> = ({ step }) => (
  <li className="flex items-start gap-2 py-1.5 text-sm">
    {step.status === "failed" ? (
      <XCircle className="mt-0.5 size-4 shrink-0 text-custom-destructive-dark" />
    ) : (
      <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-light-green" />
    )}
    <div className="flex flex-col">
      <span className="font-mono text-foreground">{step.step}</span>
      {typeof step.count === "number" ? (
        <span className="text-xs text-muted-foreground">{step.count}</span>
      ) : null}
      {step.error ? (
        <span className="text-xs text-custom-destructive-dark">
          {step.error}
        </span>
      ) : null}
    </div>
  </li>
);

const JobMeta: React.FC<{ job: DeletionJob }> = ({ job }) => {
  const { t } = useTranslation();
  return (
    <dl className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-3">
      <div className="flex flex-col">
        <dt className="text-xs text-muted-foreground">
          {t("dataDeletion.job.meta.requestedBy")}
        </dt>
        <dd className="break-all">{job.requestedByEmail ?? "—"}</dd>
      </div>
      <div className="flex flex-col">
        <dt className="text-xs text-muted-foreground">
          {t("dataDeletion.job.meta.started")}
        </dt>
        <dd>{formatDateTimeCompact(job.startedAt)}</dd>
      </div>
      <div className="flex flex-col">
        <dt className="text-xs text-muted-foreground">
          {t("dataDeletion.job.meta.completed")}
        </dt>
        <dd>{formatDateTimeCompact(job.completedAt)}</dd>
      </div>
    </dl>
  );
};

const JobProgress: React.FC<{ steps: DeletionProgressStep[] | null }> = ({
  steps,
}) => {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-semibold text-foreground">
        {t("dataDeletion.job.progressHeading")}
      </h4>
      {steps && steps.length > 0 ? (
        <ul className="divide-y rounded-lg border px-3">
          {steps.map((step, index) => (
            <ProgressRow key={`${step.step}-${index}`} step={step} />
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t("dataDeletion.job.noProgress")}
        </p>
      )}
    </div>
  );
};

const JobCounts: React.FC<{ counts: Record<string, number> }> = ({
  counts,
}) => {
  const { t } = useTranslation();
  const entries = Object.entries(counts);
  return (
    <div className="flex flex-col gap-2">
      <h4 className="text-sm font-semibold text-foreground">
        {t("dataDeletion.job.summaryHeading")}
      </h4>
      {entries.length > 0 ? (
        <ul className="rounded-lg border">
          {entries.map(([entity, count]) => (
            <li
              key={entity}
              className="flex items-center justify-between gap-3 border-b px-3 py-1.5 text-sm last:border-b-0"
            >
              <span className="font-mono text-muted-foreground">{entity}</span>
              <span className="font-medium">{count}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-sm text-muted-foreground">
          {t("dataDeletion.job.noCounts")}
        </p>
      )}
    </div>
  );
};

const JobWarnings: React.FC<{ warnings: string[] }> = ({ warnings }) => {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-light-orange/50 bg-light-orange/5 p-4">
      <div className="mb-2 flex items-center gap-2 text-light-orange">
        <TriangleAlert className="size-4" />
        <h4 className="text-sm font-semibold">
          {t("dataDeletion.job.warningsHeading")}
        </h4>
      </div>
      <p className="mb-2 text-xs text-muted-foreground">
        {t("dataDeletion.job.warningsLead")}
      </p>
      <ul className="list-disc pl-5 text-sm text-foreground">
        {warnings.map((warning, index) => (
          <li key={index}>{warning}</li>
        ))}
      </ul>
    </div>
  );
};

const JobError: React.FC<{ error: string | null }> = ({ error }) => {
  const { t } = useTranslation();
  return (
    <div className="rounded-lg border border-custom-destructive-dark/40 bg-custom-destructive-dark/5 p-4">
      <h4 className="mb-1 text-sm font-semibold text-custom-destructive-dark">
        {t("dataDeletion.job.errorHeading")}
      </h4>
      <p className="mb-2 text-xs text-muted-foreground">
        {t("dataDeletion.job.errorLead")}
      </p>
      {error ? (
        <p className="break-words text-sm text-custom-destructive-dark">
          {error}
        </p>
      ) : null}
    </div>
  );
};

const JobBody: React.FC<{ job: DeletionJob }> = ({ job }) => {
  const warnings = job.summary?.warnings ?? [];
  return (
    <>
      <JobMeta job={job} />
      <JobProgress steps={job.progress} />
      {job.status === DeletionJobStatus.COMPLETED ? (
        <JobCounts counts={job.summary?.counts ?? {}} />
      ) : null}
      {warnings.length > 0 ? <JobWarnings warnings={warnings} /> : null}
      {job.status === DeletionJobStatus.FAILED ? (
        <JobError error={job.error} />
      ) : null}
    </>
  );
};

export const DeletionJobStatusView: React.FC<DeletionJobStatusViewProps> = ({
  jobId,
  onCompleted,
  onClose,
  canCancel = true,
}) => {
  const { t } = useTranslation();
  const { data: job, isLoading, isError } = useDeletionJob(jobId);
  const cancelMutation = useCancelDeletionJob();
  const completedFiredRef = useRef(false);

  useEffect(() => {
    if (
      job?.status === DeletionJobStatus.COMPLETED &&
      !completedFiredRef.current
    ) {
      completedFiredRef.current = true;
      onCompleted?.(job);
    }
  }, [job, onCompleted]);

  const handleCancel = async () => {
    try {
      await cancelMutation.mutateAsync(jobId);
      toast.success(t("dataDeletion.job.toast.cancelled"));
    } catch (error) {
      const conflict = error instanceof ApiError && error.statusCode === 409;
      toast.error(
        conflict || !(error instanceof Error)
          ? t("dataDeletion.job.toast.cannotCancel")
          : error.message,
      );
    }
  };

  const title =
    job?.targetLabel ?? job?.targetId ?? t("dataDeletion.job.title");
  const showCancel = canCancel && job?.status === DeletionJobStatus.PENDING;

  return (
    <div className="flex flex-col gap-5">
      <DialogHeader>
        <div className="flex flex-wrap items-center gap-3">
          <DialogTitle className="break-all">{title}</DialogTitle>
          {job ? <DeletionStatusBadge status={job.status} /> : null}
          {job ? (
            <Badge variant="new_secondary">
              {t(`dataDeletion.mode.${job.mode}`)}
            </Badge>
          ) : null}
        </div>
      </DialogHeader>

      {isLoading && !job ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" />
          {t("dataDeletion.job.loading")}
        </div>
      ) : null}

      {isError && !job ? (
        <div className="rounded-md border border-custom-destructive-dark/40 bg-custom-destructive-dark/5 p-3 text-sm text-custom-destructive-dark">
          {t("dataDeletion.job.loadError")}
        </div>
      ) : null}

      {job ? <JobBody job={job} /> : null}

      <DialogFooter>
        {showCancel ? (
          <Button
            type="button"
            variant="new_destructive"
            onClick={() => void handleCancel()}
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending
              ? t("dataDeletion.job.cancelling")
              : t("dataDeletion.job.cancel")}
          </Button>
        ) : null}
        <Button type="button" variant="new_outline" onClick={onClose}>
          {t("dataDeletion.job.close")}
        </Button>
      </DialogFooter>
    </div>
  );
};
